import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessagesSquare, Check, Sparkles } from "lucide-react";
import { V } from "@/lib/vocab";

export default function TeachDemo() {
  const [answer, setAnswer] = useState("");
  const [saved, setSaved] = useState(false);

  const save = () => {
    if (!answer.trim() || saved) return;
    setSaved(true);
    setTimeout(() => { setSaved(false); setAnswer(""); }, 4200);
  };

  return (
    <div className="relative rounded-2xl border border-amber-200 bg-amber-50/60 p-5 overflow-hidden">
      <div className="flex items-start gap-2">
        <MessagesSquare className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" strokeWidth={1.75} />
        <div>
          <p className="font-semibold text-ink leading-snug">"Can I have guests park overnight in the visitor lot?"</p>
          <p className="text-[11px] text-amber-700 font-semibold mt-1">Asked 3 times this week</p>
        </div>
      </div>

      <textarea data-testid="teach-answer-input" value={answer} onChange={(e) => setAnswer(e.target.value)}
        rows={2} placeholder="Type the answer your concierge should give…"
        className="mt-3 w-full rounded-xl border border-sand-200 bg-white px-3 py-2 text-sm text-ink placeholder:text-ink-400 outline-none focus:ring-2 focus:ring-amber-400/40 resize-none" />

      <div className="mt-3 flex items-center gap-2">
        <button data-testid="teach-save-btn" onClick={save} disabled={!answer.trim()}
          className="inline-flex items-center gap-1.5 rounded-full bg-ink text-white text-sm font-semibold px-4 py-2 hover:bg-ink-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
          <Check className="h-4 w-4" /> Save answer
        </button>
        <button onClick={() => setAnswer("")}
          className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 text-ink-600 text-sm font-medium px-4 py-2 hover:bg-white transition-colors">
          Dismiss
        </button>
      </div>

      <AnimatePresence>
        {saved && (
          <motion.div data-testid="teach-success" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-amber-50/95 backdrop-blur-sm text-center px-6">
            <motion.div initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 320, damping: 15 }}
              className="h-14 w-14 rounded-2xl bg-emerald-500 flex items-center justify-center shadow-lg mb-3">
              <Check className="h-8 w-8 text-white" strokeWidth={3} />
            </motion.div>
            <motion.p initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.12 }}
              className="font-heading text-lg font-extrabold text-ink flex items-center gap-1.5">
              <Sparkles className="h-4 w-4 text-amber-500" /> You just made your {V.lc("org")}'s AI smarter!
            </motion.p>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} className="text-sm text-ink-600 mt-1">
              It'll answer this instantly from now on.
            </motion.p>
            {[...Array(7)].map((_, i) => (
              <motion.span key={i} className="absolute h-1.5 w-1.5 rounded-full bg-amber-400" style={{ left: "50%", top: "36%" }}
                initial={{ opacity: 1, x: 0, y: 0, scale: 1 }}
                animate={{ opacity: 0, x: Math.cos((i / 7) * 6.283) * 80, y: Math.sin((i / 7) * 6.283) * 80, scale: 0 }}
                transition={{ duration: 0.9, delay: 0.1 }} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
